import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
} from '@mui/material';
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { StyleObj } from '../../@types';
import { CasinoUserRow, CasinoUsersQueryParams } from '../../@types/casino';
import { useModal } from '../../contexts/ModalContext';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { getCasinoFreeSpinsAggregator } from '../../helpers/casino';
import usePermissions from '../../hooks/usePermissions';
import { useCasinoMarketplace, useCasinoUsers } from '../../queries/casino';
import { createColumn } from '../../utils';
import FormModal from '../modals/FormModal';
import TableTemplate from '../templates/TableTemplate';

const DEFAULT_SORT_COLUMN = 'username';
const DEFAULT_SORT_ORDER = 'asc';

const DEFAULT_QUERY_PARAMS: CasinoUsersQueryParams = {
  page: 1,
  limit: 25,
  accessLevel: '1',
  sortColumn: DEFAULT_SORT_COLUMN,
  sortOrder: DEFAULT_SORT_ORDER,
};

const ACCESS_LEVEL_OPTIONS = ['1', '2', '3', '4'];

const styles: StyleObj = {
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: '100%',
  },
  accessLevelSelect: {
    minWidth: 180,
  },
};

const CasinoUsersPage = () => {
  const { openModal } = useModal();
  const { hasPermission } = usePermissions();
  const marketplace = useCasinoMarketplace();
  const canAwardGga =
    hasPermission('defaultAdminRole') &&
    !marketplace.isError &&
    getCasinoFreeSpinsAggregator(marketplace.data) === 'GGA';
  const { queryParams, paginationModel, updateQueryParams, handleSearch, handleSort, changeQuery } =
    useCasinoTableQuery<CasinoUsersQueryParams>({
      defaultQuery: DEFAULT_QUERY_PARAMS,
      searchField: 'search',
      emptyFields: ['search'],
      sortDefaults: {
        sortColumn: 'sortColumn',
        sortOrder: 'sortOrder',
        defaultColumn: DEFAULT_SORT_COLUMN,
        defaultOrder: DEFAULT_SORT_ORDER,
      },
    });
  const { data: casinoUsersData, isFetching, isLoading } = useCasinoUsers(queryParams);

  const openFreeSpinsDialog = useCallback(
    (player: CasinoUserRow) => {
      openModal(<FormModal form='gga-free-spins' item={player} />);
    },
    [openModal]
  );

  const columns = useMemo<GridColDef<CasinoUserRow>[]>(
    () => [
      createColumn('username', 'Username', {
        minWidth: 180,
      }),
      createColumn('entity', 'Entity', {
        minWidth: 180,
        valueGetter: (_value, row, _column, _apiRef) => row.entity?.parentEntityId ?? row.parentEntityId ?? '-',
      }),
      createColumn('accessLevel', 'Access Level', {
        minWidth: 140,
        sortable: false,
      }),
      createColumn('actions', 'Actions', {
        minWidth: 100,
        sortable: false,
        disableExport: true,
        renderCell: ({ row }: GridCellParams<CasinoUserRow>) => {
          if (!canAwardGga) return null;
          const isPlayer = Number(row.accessLevel) === 1;

          return (
            <Tooltip title={isPlayer ? 'Give free spins' : 'Free spins are only available for players'}>
              <span>
                <IconButton
                  aria-label={`Give free spins to ${row.username ?? row._id}`}
                  color='primary'
                  disabled={!isPlayer}
                  onClick={() => openFreeSpinsDialog(row)}
                >
                  <CardGiftcardOutlined />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      }),
    ],
    [openFreeSpinsDialog, canAwardGga]
  );

  const handleAccessLevelChange = useCallback(
    (event: SelectChangeEvent) => {
      changeQuery({
        accessLevel: event.target.value,
      });
    },
    [changeQuery]
  );

  return (
    <TableTemplate
      rows={casinoUsersData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={casinoUsersData?.count || 0}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      showFilter={false}
      paginationModel={paginationModel}
      customTableHeaderContent={
        <Stack sx={styles.headerContent}>
          <FormControl size='small' sx={styles.accessLevelSelect}>
            <InputLabel id='casino-users-access-level-label'>Access level</InputLabel>
            <Select
              labelId='casino-users-access-level-label'
              id='casino-users-access-level'
              label='Access level'
              value={queryParams.accessLevel}
              onChange={handleAccessLevelChange}
            >
              {ACCESS_LEVEL_OPTIONS.map((accessLevel) => (
                <MenuItem key={accessLevel} value={accessLevel}>
                  {accessLevel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      }
    />
  );
};

export default CasinoUsersPage;
